<script setup lang="ts">
import api from "@/api";
import type { TUserData } from "@/api/types";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { useSessionStore } from "@/store/session";
import { useDefaultStore } from "@/store";
import type { IEditUserParams } from "@/api/types";

definePage({
	name: "ProfilePage",
	meta: {
		name: "Profile",
	},
});

const toast = useToast();
const sessionStore = useSessionStore();
const store = useDefaultStore();
const route = useRoute();
const initialUserData = ref<TUserData | null>(null);
const newProfilePictureFile = ref<File | null>(null);
const submitLoading = ref(false);
const profilePictureDialogVisible = ref(false);
const currentUserId = ref(-1);
const isSubmitted = ref(false);
const changePasswordDialogVisible = ref(false);
const oldPassword = ref<string | null>(null);
const newPassword = ref<string | null>(null);
const newPasswordConfirm = ref<string | null>(null);

const isValidChangePasswordForm = computed(() => {
	const hasFields = !!(
		oldPassword.value &&
		oldPassword.value.trim() &&
		newPassword.value &&
		newPassword.value.trim() &&
		newPasswordConfirm.value &&
		newPasswordConfirm.value.trim()
	);
	if (hasFields) return true;
	return false;
});

async function getIdFromPath() {
	const params = route.params as { id: string | string[] };

	if (Array.isArray(params.id)) {
		const id = parseInt(params.id[0], 10);
		if (!isNaN(id) && id > 0) {
			return id;
		}
	} else {
		const id = parseInt(params.id, 10);
		if (!isNaN(id) && id > 0) {
			return id;
		}
	}
}

function cancelEditEmail(closeCallback: () => void) {
	sessionStore.userData.usr_email = initialUserData.value.usr_email;
	closeCallback();
}

function cancelEditDisplayname(closeCallback: () => void) {
	sessionStore.userData.usr_displayname = initialUserData.value.usr_displayname;
	closeCallback();
}

async function editUser(params: IEditUserParams, closeCallback?: () => void) {
	if (params.usr_password_new) {
		console.log(params.usr_password_new);
		if (!store.passwordRegex.test(params.usr_password_new)) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.passwordFormat.title"),
				detail: $t("app.userEdit.error.passwordFormat.msg"),
				life: 3000,
			});
			return;
		}
	}
	if (
		params.usr_email ||
		params.usr_email === null ||
		params.usr_email === ""
	) {
		if (!params.usr_email.trim()) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.requiredName.title"),
				detail: $t("app.userEdit.error.requiredName.msg"),
				life: 3000,
			});
			return;
		}
	}

	if (
		params.usr_displayname ||
		params.usr_displayname === null ||
		params.usr_displayname === ""
	) {
		if (!params.usr_displayname.trim()) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.requiredName.title"),
				detail: $t("app.userEdit.error.requiredName.msg"),
				life: 3000,
			});
			return;
		}
	}

	const formData = new FormData();

	formData.append("usr_id", sessionStore.userData.usr_id.toString());

	if (params.profile_picture) {
		if (
			sessionStore.userData.profile_picture !==
			initialUserData.value.profile_picture
		) {
			formData.append("profile_picture", newProfilePictureFile.value);
		}
	}

	if (params.usr_email) {
		formData.append("usr_email", params.usr_email);
		closeCallback();
	}

	if (params.usr_displayname) {
		formData.append("usr_displayname", params.usr_displayname);
		closeCallback();
	}

	if (params.usr_password_new && params.usr_password_old) {
		formData.append("usr_password_new", params.usr_password_new);
		formData.append("usr_password_old", params.usr_password_old);
	}

	submitLoading.value = true;

	try {
		const res = await api.usersEdit(formData);
		if (params.profile_picture) {
			toast.add({
				severity: "success",
				summary: $t("app.userEdit.success.changeProfilePicture.title"),
				detail: $t("app.userEdit.success.changeProfilePicture.msg"),
				life: 3000,
			});
			sessionStore.userData.profile_picture =
				sessionStore.userData.profile_picture;
		}
		if (params.usr_displayname) {
			toast.add({
				severity: "success",
				summary: $t("app.userEdit.success.changeDisplayName.title"),
				detail: $t("app.userEdit.success.changeDisplayName.msg"),
				life: 3000,
			});
		}
		if (params.usr_email) {
			toast.add({
				severity: "success",
				summary: $t("app.userEdit.success.changeEmail.title"),
				detail: $t("app.userEdit.success.changeEmail.msg"),
				life: 3000,
			});
		}
		if (params.usr_password_new && params.usr_password_old) {
			toast.add({
				severity: "success",
				summary: $t("app.userEdit.success.changePassword.title"),
				detail: $t("app.userEdit.success.changePassword.msg"),
				life: 3000,
			});
			changePasswordDialogVisible.value = false;
		}
		sessionStore.renewToken();
		initialUserData.value = JSON.parse(JSON.stringify(sessionStore.userData));
		console.warn(res.data);
	} catch (err: any) {
		if (err.response.data.message.includes("User not found.")) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.userNotFound.title"),
				detail: $t("app.userEdit.error.userNotFound.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("No update params.")) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.noUpdateParams.title"),
				detail: $t("app.userEdit.error.noUpdateParams.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Name is required.")) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.requiredName.title"),
				detail: $t("app.userEdit.error.requiredName.msg"),
				life: 3000,
			});
		} else if (
			err.response.data.message.includes("The email is already taken.")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.userEdit.error.emailUnique.title"),
				detail: $t("app.userEdit.error.emailUnique.msg"),
				life: 3000,
			});
		} else if (
			err.response.data.message.includes("Token has been blacklisted.")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.tokenBlacklisted.title"),
				detail: $t("app.auth.error.tokenBlacklisted.msg"),
				life: 3000,
			});
			try {
				await sessionStore.logoutApi();
			} catch (err: any) {
				console.warn(err.message);
			}
		} else if (err.response.data.message.includes("Account is suspended.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.accSuspended.title"),
				detail: $t("app.auth.error.accSuspended.msg"),
				life: 3000,
			});
		} else {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.default.title"),
				detail: $t("app.postForm.error.default.msg"),
				life: 3000,
			});
		}
		sessionStore.userData.profile_picture =
			initialUserData.value.profile_picture;
		console.warn(err.message);
	} finally {
		submitLoading.value = false;
		isSubmitted.value = false;
	}
}

async function changePassword() {
	if (newPassword.value !== newPasswordConfirm.value) {
		toast.add({
			severity: "error",
			summary: $t("app.userEdit.error.passwordsMatch.title"),
			detail: $t("app.userEdit.error.passwordsMatch.msg"),
			life: 3000,
		});
		return;
	}

	await editUser({
		usr_password_new: newPassword.value,
		usr_password_old: oldPassword.value,
	});
}

function onImageSelect($event) {
	const file = $event.files[0];

	if (file.size > 1500000) {
		toast.add({
			severity: "warn",
			summary: $t("app.postForm.warn.imageSize.title"),
			detail: $t("app.postForm.warn.imageSize.msg"),
			life: 3000,
		});
	}

	const previewUrl = URL.createObjectURL(file);
	sessionStore.userData.profile_picture = previewUrl;
	newProfilePictureFile.value = file;
}

async function onClickEditProfilePicture() {
	isSubmitted.value = true;
	profilePictureDialogVisible.value = false;
	await editUser({ profile_picture: sessionStore.userData.profile_picture });
}

watch(
	() => profilePictureDialogVisible.value,
	(val) => {
		if (!val && !isSubmitted.value) {
			if (
				sessionStore.userData?.profile_picture &&
				initialUserData.value?.profile_picture
			) {
				sessionStore.userData.profile_picture =
					initialUserData.value.profile_picture;
			}
		}
	},
);

onMounted(async () => {
	currentUserId.value = await getIdFromPath();

	if (sessionStore.userData.usr_id === currentUserId.value) {
		initialUserData.value = JSON.parse(JSON.stringify(sessionStore.userData));
	}
});
</script>

<template>
	<div class="profile-outer-wrapper admin-section-outer-wrapper">
		<div
			v-if="sessionStore.userData"
			class="profile-inner-wrapper admin-section-inner-wrapper"
		>
			<div class="profile-left-wrapper">
				<div class="admin-section-header-wrapper">
					<div class="admin-section-header-heading">
						{{ $t("app.userEdit.title") }}
					</div>
				</div>
				<div class="profile-left-profile-picture-wrapper">
					<div
						class="profile-left-profile-picture-container"
						@click="profilePictureDialogVisible = true"
					>
						<Avatar
							:image="sessionStore.userData.profile_picture || ''"
							:label="
								!sessionStore.userData.profile_picture
									? store.getFirstLetter(sessionStore.userData.usr_displayname)
									: ''
							"
							shape="circle"
							:pt="{
								root: {
									style: 'width: calc(100% - 5px); height: calc(100% - 5px);',
								},
								label: {
									style:
										'height: 80%; width: 80%; display: flex; justify-content: center; align-items: center; font-size: 7rem;',
								},
								image: {
									style: `width: 100%; height: 100%; object-fit: cover;`,
								},
							}"
						/>
						<div class="profile-left-profile-picture-edit">
							<i-fa-pen
								class="profile-left-profile-picture-edit-icon"
							></i-fa-pen>
						</div>
						<Dialog
							v-model:visible="profilePictureDialogVisible"
							modal
							:header="$t('app.userEdit.editProfilePicture')"
							:style="{
								width: '100%',
								'max-width': '600px',
							}"
							:pt="{
								content: {
									style:
										'padding: 20px; display: flex; flex-direction: column; align-items: center;',
								},
							}"
						>
							<FileUpload
								mode="basic"
								@select="onImageSelect($event)"
								customUpload
								auto
							/>
							<Avatar
								:image="sessionStore.userData.profile_picture || ''"
								:label="
									!sessionStore.userData.profile_picture
										? store.getFirstLetter(
												sessionStore.userData.usr_displayname,
											)
										: ''
								"
								shape="circle"
								:pt="{
									root: {
										style: 'width: unset; height: unset;',
									},
									label: {
										style: `border-radius: 50%; 
											background-color: rgba(44, 117, 244, 0.231); 
											width: 400px; 
											max-width: 90%; 
											aspect-ratio: 1/1; 
											margin-top: 20px; 
											display: flex; 
											justify-content: center; 
											align-items: center; 
											font-size: 10rem;`,
									},
									image: {
										style: `width: 400px; max-width: 90%; aspect-ratio: 1/1; margin-top: 20px; object-fit: cover; background-color: grey;`,
									},
								}"
							/>
							<template #footer>
								<Button
									type="button"
									:label="$t('app.basic.cancel')"
									severity="secondary"
									@click="profilePictureDialogVisible = false"
								></Button>
								<Button
									type="button"
									label="Save"
									@click="onClickEditProfilePicture()"
									:disabled="
										sessionStore.userData.profile_picture ===
										initialUserData.profile_picture
									"
								></Button>
							</template>
						</Dialog>
					</div>
				</div>
				<div class="profile-left-info-wrapper">
					<div class="profile-left-info-group">
						<div class="profile-left-display-name-label">
							{{ $t("app.userEdit.displayname") }}
						</div>
						<Inplace>
							<template #display>
								{{ sessionStore.userData.usr_displayname }}
							</template>
							<template #content="{ closeCallback }">
								<span>
									<InputText
										v-model="sessionStore.userData.usr_displayname"
										autofocus
									/>
									<Button
										:label="$t('app.basic.cancel')"
										variant="outlined"
										severity="secondary"
										@click="cancelEditDisplayname(closeCallback)"
									/>
									<Button
										v-if="
											sessionStore.userData.usr_displayname !==
											initialUserData.usr_displayname
										"
										:label="$t('app.basic.submit')"
										severity="info"
										@click="
											editUser(
												{
													usr_displayname:
														sessionStore.userData.usr_displayname,
												},
												closeCallback,
											)
										"
									/>
								</span>
							</template>
						</Inplace>
					</div>
					<div class="profile-left-info-group">
						<div class="profile-left-display-name-label">
							{{ $t("app.userEdit.email") }}
						</div>
						<Inplace v-if="sessionStore.userData.usr_email">
							<template #display>
								{{ sessionStore.userData.usr_email }}
							</template>
							<template #content="{ closeCallback }">
								<span>
									<InputText
										v-model="sessionStore.userData.usr_email"
										type="email"
										autofocus
									/>
									<Button
										:label="$t('app.basic.cancel')"
										variant="outlined"
										severity="secondary"
										@click="cancelEditEmail(closeCallback)"
									/>
									<Button
										v-if="
											sessionStore.userData.usr_email !==
											initialUserData.usr_email
										"
										:label="$t('app.basic.submit')"
										severity="info"
										@click="
											editUser(
												{ usr_email: sessionStore.userData.usr_email },
												closeCallback,
											)
										"
									/>
								</span>
							</template>
						</Inplace>
					</div>
					<div class="profile-left-info-group">
						<div class="profile-left-display-name-label">
							{{ $t("app.userEdit.passwordLabel") }}
						</div>
						<Button
							:label="$t('app.userEdit.changePassword.title')"
							severity="secondary"
							:pt="{
								root: {
									style: 'min-width: 149px;',
								},
							}"
							@click="changePasswordDialogVisible = true"
						/>
						<Dialog
							v-model:visible="changePasswordDialogVisible"
							modal
							:header="$t('app.userEdit.changePassword.title')"
							:style="{ width: '25rem' }"
						>
							<div class="create-user-input-wrapper">
								<label
									for="password-old"
									class="create-user-label"
								>
									{{ $t("app.userEdit.oldPassword") }}*
								</label>
								<Password
									id="password-old"
									v-model="oldPassword"
									:autofocus="true"
									toggleMask
									:feedback="false"
									fluid
									@keyup.enter="changePassword()"
								/>
							</div>
							<div class="create-user-input-wrapper">
								<label
									for="new-password"
									class="create-user-label"
								>
									{{ $t("app.userEdit.newPassword") }}*
								</label>
								<Password
									id="new-password"
									v-model="newPassword"
									toggleMask
									fluid
									:pt="{
										content: {
											style: 'display: none',
										},
									}"
									@keyup.enter="changePassword()"
								>
									<template #footer>
										<ul class="pl-2 ml-2 my-0 leading-normal">
											<li>
												{{ $t("app.userEdit.changePassword.conditions.con1") }}
											</li>
											<li>
												{{ $t("app.userEdit.changePassword.conditions.con2") }}
											</li>
											<li>
												{{ $t("app.userEdit.changePassword.conditions.con3") }}
											</li>
											<li>
												{{ $t("app.userEdit.changePassword.conditions.con4") }}
											</li>
											<li>
												{{ $t("app.userEdit.changePassword.conditions.con5") }}
											</li>
										</ul>
									</template>
								</Password>
							</div>
							<div class="create-user-input-wrapper">
								<label
									for="new-password"
									class="create-user-label"
								>
									{{ $t("app.userEdit.newPasswordConfirm") }}*
								</label>
								<Password
									id="new-password"
									v-model="newPasswordConfirm"
									toggleMask
									:feedback="false"
									fluid
									:invalid="
										newPasswordConfirm && newPassword !== newPasswordConfirm
									"
									@keyup.enter="changePassword()"
								/>
							</div>
							<div class="create-user-buttons-wrapper">
								<Button
									type="button"
									:label="$t('app.basic.cancel')"
									severity="secondary"
									@click="changePasswordDialogVisible = false"
									:pt="{
										root: {
											style: 'margin-right: 9px;',
										},
									}"
								></Button>
								<Button
									:disabled="!isValidChangePasswordForm"
									type="button"
									:label="$t('app.basic.submit')"
									@click="changePassword()"
									:loading="submitLoading"
								></Button>
							</div>
						</Dialog>
					</div>
				</div>
			</div>
			<div class="profile-center-wrapper">
				<create-user v-if="sessionStore.can('create-user')" />
			</div>
			<div class="profile-right-wrapper"></div>
		</div>
		<div
			v-else
			style="
				display: flex;
				justify-content: center;
				align-items: center;
				height: 100%;
			"
		>
			User not found.
		</div>
	</div>
</template>

<style lang="scss" scoped>
.profile-outer-wrapper {
	.profile-inner-wrapper {
		display: flex;
		flex-wrap: wrap;

		.profile-left-wrapper,
		.profile-center-wrapper,
		.profile-right-wrapper {
			width: 100%;
		}

		.profile-center-wrapper {
			padding: 24px 0;
			height: 65px;
			display: flex;
			align-items: center;
			min-width: 200px;
		}

		.profile-left-wrapper {
			.profile-left-profile-picture-wrapper {
				display: flex;
				// justify-content: center;
				margin-bottom: 40px;
				.profile-left-profile-picture-container {
					width: 182px;
					aspect-ratio: 1 / 1;
					background-color: $admin-accent-color-light;
					max-width: 90%;
					border-radius: 100%;
					display: flex;
					justify-content: center;
					align-items: center;
					position: relative;
					overflow: hidden;
					cursor: pointer;

					.profile-left-profile-picture {
						width: 172px;
						aspect-ratio: 1 / 1;
						border-radius: 100%;
						background-color: $admin-accent-color;
						background-position: center;
						background-repeat: no-repeat;
						background-size: cover;
					}

					.profile-left-profile-picture-edit {
						width: 100%;
						height: 30px;
						position: absolute;
						bottom: 0;
						left: 0;
						background-color: rgba($admin-text-color, 0.5);
						display: flex;
						justify-content: center;
						align-items: center;

						.profile-left-profile-picture-edit-icon {
							height: 90%;
							filter: brightness(0) saturate(100%) invert(100%) sepia(100%)
								saturate(15%) hue-rotate(234deg) brightness(103%) contrast(100%);
						}
					}
				}
			}

			.profile-left-info-wrapper {
				display: flex;
				flex-direction: column;
				gap: 20px;
				.profile-left-info-group {
					.profile-left-display-name-label {
						font-size: 12px;
						font-weight: 600;
						line-height: 14.06px;
						color: $admin-text-color-light;
						margin-bottom: 6px;
						user-select: none;
					}
				}
			}
		}
	}
}

@media screen and (width >= 768px) {
	.profile-outer-wrapper {
		.profile-inner-wrapper {
			.profile-left-wrapper {
				flex: 1;
				margin-right: 24px;
				margin-bottom: 24px;

				.profile-left-profile-picture-wrapper {
					.profile-left-profile-picture-container {
						max-width: 100%;
						.profile-left-profile-picture-edit {
							transition: transform 0.3s ease;
							transform: translateY(100%);
						}

						&:hover {
							.profile-left-profile-picture-edit {
								transform: translateY(0);
							}
						}
					}
				}
				.profile-left-info-wrapper {
					.profile-left-info-group {
						.profile-left-display-name-label {
							font-size: 12px;
							font-weight: 600;
							line-height: 14.06px;
							color: $admin-text-color-light;
							margin-bottom: 6px;
							user-select: none;
						}
					}
				}
			}

			.profile-center-wrapper,
			.profile-right-wrapper {
				max-width: 500px;
			}
		}
	}
}
</style>
